/**
 * Shared presentation helpers for notification feeds.
 *
 * Backend items come back as { _id, title, body, type, isRead, createdAt },
 * so anything rendering a feed needs the same relative-time and icon mapping.
 */

export const formatNotificationTime = (createdAt) => {
  if (!createdAt) return "Just now";
  const parsed = new Date(createdAt);
  const now = new Date();
  const diffMs = now - parsed;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

/** Shown when a notification is not about a person, or the person has no photo. */
export const NOTIFICATION_FALLBACK_IMAGE = "/images/logo.png";

/**
 * The picture to show beside a notification.
 *
 * This used to map the notification *type* to a stock photo, so every message
 * — whoever sent it — was captioned with the same portrait of an unrelated
 * person, and every booking with the same stock bedroom. The API now resolves
 * the real actor, so the only choices left are their photo or an honest
 * fallback: never a stranger's face.
 */
export const getNotificationAvatar = (notification) =>
  notification?.actor?.avatarUrl || null;

/** Booking notifications a traveller receives, all about one of their bookings. */
const TRAVELLER_BOOKING_TYPES = new Set([
  "booking_accepted",
  "booking_rejected",
  "booking_cancelled",
  "booking_alert",
  "booking_status",
  "trip_started",
  "trip_completed",
  "trip_start_pending",
  "trip_end_pending",
]);

/**
 * Where clicking a notification should take the viewer, or null to stay put.
 *
 * `trip_start_pending` / `trip_end_pending` ask the traveller to confirm their
 * half of the handshake that releases the assistant's fee, so they have to land
 * on that booking's detail, where the confirm button lives. Assistants receive
 * the same types but manage bookings from their own dashboard, so they get no
 * link to the traveller's My Trips page.
 */
export const getNotificationLink = (notification, role) => {
  if (role !== "user") return null;
  if (!TRAVELLER_BOOKING_TYPES.has(notification?.type)) return null;
  const bookingId = notification?.metadata?.bookingId;
  if (!bookingId) return null;
  return `/user/trips/${encodeURIComponent(String(bookingId))}`;
};

/** Initials for an actor with no photo, e.g. "King James" -> "KJ". */
export const getNotificationInitials = (notification) => {
  const name = notification?.actor?.name?.trim();
  if (!name) return "";
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
};
